import { BigNumber } from '@ethersproject/bignumber'
import { defaultAbiCoder } from '@ethersproject/abi'
import {
  Currency,
  CurrencyAmount,
  ETHER,
  Token,
} from '@pancakeswap/sdk'
import { useCallback, useMemo } from 'react'

import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useGasPrice } from 'state/user/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import {
  calculateGasMargin,
  getAggregatorContract,
} from 'utils'

import {
  findBestRobinhoodSmartRoute,
  RobinhoodSmartRoute,
} from './useRobinhoodSmartRoute'

import {
  BPS_DENOMINATOR,
  ROBINHOOD_MAINNET_V3_ADAPTER,
  ROBINHOOD_MAINNET_WETH,
} from 'config/robinhood/smartRouting'

interface Params {
  route: RobinhoodSmartRoute | null
  inputAmount?: CurrencyAmount
  inputCurrency?: Currency
  outputCurrency?: Currency
  recipient?: string | null
  allowedSlippage: number
}

function sameRoute(
  displayed: RobinhoodSmartRoute,
  fresh: RobinhoodSmartRoute,
): boolean {
  if (displayed.kind !== fresh.kind) {
    return false
  }

  if (displayed.kind === 'v3' && fresh.kind === 'v3') {
    return displayed.fee === fresh.fee
  }

  if (displayed.kind === 'v2' && fresh.kind === 'v2') {
    if (
      displayed.router.toLowerCase() !==
      fresh.router.toLowerCase()
    ) {
      return false
    }

    if (displayed.path.length !== fresh.path.length) {
      return false
    }

    return displayed.path.every(
      (token, index) =>
        token.toLowerCase() ===
        fresh.path[index].toLowerCase(),
    )
  }

  return false
}

export function useRobinhoodSmartSwapCallback({
  route,
  inputAmount,
  inputCurrency,
  outputCurrency,
  recipient,
  allowedSlippage,
}: Params) {
  const { account, chainId, library } = useActiveWeb3React()
  const deadline = useTransactionDeadline()
  const gasPrice = useGasPrice()
  const addTransaction = useTransactionAdder()

  const enabled =
    chainId === 4663 &&
    Boolean(
      account &&
        library &&
        deadline &&
        route &&
        inputAmount &&
        inputCurrency &&
        outputCurrency &&
        recipient,
    )

  const callback = useCallback(async () => {
    if (
      !enabled ||
      !account ||
      !library ||
      !deadline ||
      !route ||
      !inputAmount ||
      !inputCurrency ||
      !outputCurrency ||
      !recipient
    ) {
      throw new Error(
        'Missing Robinhood swap dependencies',
      )
    }

    if (
      allowedSlippage < 0 ||
      allowedSlippage >= BPS_DENOMINATOR
    ) {
      throw new Error('Invalid slippage tolerance')
    }

    const amountIn = BigNumber.from(
      inputAmount.raw.toString(),
    )

    const inputIsNative = inputCurrency === ETHER
    const outputIsNative = outputCurrency === ETHER

    const tokenIn = inputIsNative
      ? ROBINHOOD_MAINNET_WETH
      : inputCurrency instanceof Token
      ? inputCurrency.address
      : undefined

    const tokenOut = outputIsNative
      ? ROBINHOOD_MAINNET_WETH
      : outputCurrency instanceof Token
      ? outputCurrency.address
      : undefined

    if (!tokenIn || !tokenOut) {
      throw new Error(
        'Unsupported Robinhood currency',
      )
    }

    /*
     * Freshly search every supported venue again immediately
     * before simulation/submission.
     */
    const freshRoute =
      await findBestRobinhoodSmartRoute(
        library,
        amountIn,
        tokenIn,
        tokenOut,
      )

    if (!freshRoute) {
      throw new Error(
        'No executable route is currently available. Please refresh the quote and try again.',
      )
    }

    /*
     * Never silently switch the user to another venue or fee tier.
     * The UI must refresh and show the new winning route first.
     */
    if (!sameRoute(route, freshRoute)) {
      throw new Error(
        'The best route changed. Please review the updated quote and try again.',
      )
    }

    /*
     * The minimum is based on the quote the user actually reviewed,
     * not a newly lowered quote.
     */
    const amountOutMin = route.amountOut
      .mul(BPS_DENOMINATOR - allowedSlippage)
      .div(BPS_DENOMINATOR)

    if (amountOutMin.isZero()) {
      throw new Error('Minimum output is zero')
    }

    /*
     * If the freshly executable quote has already moved below the
     * user's confirmed slippage floor, stop before wallet submission.
     */
    if (freshRoute.amountOut.lt(amountOutMin)) {
      throw new Error(
        'Price moved beyond your slippage tolerance. Please review the updated quote and try again.',
      )
    }

    const aggregator = getAggregatorContract(
      library,
      account,
    )

    const deadlineValue = deadline.toNumber()

    let methodName: string
    let args: any[]
    let value: string | undefined

    if (freshRoute.kind === 'v3') {
      const data = defaultAbiCoder.encode(
        ['uint24'],
        [freshRoute.fee],
      )

      if (inputIsNative) {
        methodName = 'executeAdapterNativeSwap'

        args = [
          ROBINHOOD_MAINNET_V3_ADAPTER,
          tokenOut,
          amountOutMin.toString(),
          recipient,
          deadlineValue,
          data,
        ]

        value = amountIn.toString()
      } else if (outputIsNative) {
        methodName =
          'executeAdapterTokenForNativeSwap'

        args = [
          ROBINHOOD_MAINNET_V3_ADAPTER,
          tokenIn,
          amountIn.toString(),
          amountOutMin.toString(),
          recipient,
          deadlineValue,
          data,
        ]
      } else {
        methodName = 'executeAdapterTokenSwap'

        args = [
          ROBINHOOD_MAINNET_V3_ADAPTER,
          tokenIn,
          tokenOut,
          amountIn.toString(),
          amountOutMin.toString(),
          recipient,
          deadlineValue,
          data,
        ]
      }
    } else if (inputIsNative) {
      methodName = 'swapExactETHForTokens'

      args = [
        freshRoute.router,
        amountOutMin.toString(),
        freshRoute.path,
        recipient,
        deadlineValue,
      ]

      value = amountIn.toString()
    } else if (outputIsNative) {
      methodName = 'swapExactTokensForETH'

      args = [
        freshRoute.router,
        amountIn.toString(),
        amountOutMin.toString(),
        freshRoute.path,
        recipient,
        deadlineValue,
      ]
    } else {
      methodName = 'swapExactTokensForTokens'

      args = [
        freshRoute.router,
        amountIn.toString(),
        amountOutMin.toString(),
        freshRoute.path,
        recipient,
        deadlineValue,
      ]
    }

    const overrides = value ? { value } : {}

    /*
     * Simulate the exact transaction that will be submitted.
     * Any revert stops here before the wallet transaction.
     */
    await aggregator.callStatic[methodName](
      ...args,
      overrides,
    )

    const gasEstimate =
      await aggregator.estimateGas[methodName](
        ...args,
        overrides,
      )

    const response =
      await aggregator[methodName](...args, {
        ...overrides,
        gasLimit: calculateGasMargin(gasEstimate),
        gasPrice,
        from: account,
      })

    const inputSymbol =
      inputCurrency === ETHER
        ? 'ETH'
        : inputCurrency.symbol

    const outputSymbol =
      outputCurrency === ETHER
        ? 'ETH'
        : outputCurrency.symbol

    addTransaction(response, {
      summary:
        `Swap ${inputAmount.toSignificant(3)} ` +
        `${inputSymbol} for ${outputSymbol}`,
    })

    return response.hash
  }, [
    account,
    addTransaction,
    allowedSlippage,
    deadline,
    enabled,
    gasPrice,
    inputAmount,
    inputCurrency,
    library,
    outputCurrency,
    recipient,
    route,
  ])

  return useMemo(
    () => ({
      callback: enabled ? callback : null,
      error: enabled
        ? null
        : 'Robinhood smart route unavailable',
    }),
    [callback, enabled],
  )
}
